/**
 * ChatPanel — panel deslizante derecho con conversación Claude inline.
 *
 * Backend: POST /api/v1/chat con loadProviderFromAgenteCanonico (DT-11).
 * Auto-incluye contexto actual de la ruta (entidad / instancia) si toggle activo.
 *
 * Estado persiste localStorage (últimos 30 mensajes). Reset al logout.
 *
 * ASR/AF-1 (orden 07156a35 ASISTENTE-ARQUITECTO-INFORMES-FRONT): el asistente es
 * el cerebro CONVERSACIONAL — solo devuelve TEXTO, nunca actúa (frontera dual
 * inviolable). Cuando el texto trae un bloque ```html autocontenido (informe con
 * gráfico inline + tabla, que produce el system prompt acotado del A-back), el
 * FRONT lo presenta: tarjeta → visor FULL-PAGE (iframe srcDoc, patrón VisorAppPage)
 * con Descargar PDF (print-to-PDF del navegador), Compartir (Web Share / copiar) y
 * el propio asistente expandible a pantalla completa. Cero lógica de cerebro: solo
 * presentación de lo que el texto ya contiene.
 */
import { useState, useRef, useEffect, useMemo, type FormEvent } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { X, Send, Sparkles, Clock, Cpu, Maximize2, Minimize2, FileText, FileDown, Share2, History, Plus, MessageSquare, ChevronDown, ChevronRight, Check } from 'lucide-react'
import { toast } from 'sonner'
import { useChatStore, type ChatMessage, type PropuestaEntidad } from '@/stores/chat'
import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'
import { api, ApiError } from '@/lib/api'
import { explicarError, toastError } from '@/lib/errores'
import { getAvailableActions } from '@/lib/mcp'
import { ejecutarVerboCanonico } from '@/lib/acciones'
import { obtenerCatalogoIntenciones, validarMensajeIntencion, type CatalogoIntenciones } from '@/lib/intenciones'
import { listarConversaciones, cargarConversacion, cargarHtmlInforme, type ConversacionResumen } from '@/lib/conversaciones'
import { listarAgentesActivos, type AgenteModelo } from '@/lib/agentes'
import { logAarpia } from '@/lib/log'
import { AccionesInstancia } from './AccionesInstancia'
import { PropuestaEntidadCard } from './PropuestaEntidadCard'
import { Button } from './ui/button'

interface ChatResponse {
  respuesta: string
  agente: { id: string; slug: string; modelo: string }
  tokens?: { input?: number; output?: number }
  latencia_ms: number
  /** F3: hilo gobernado que abrió/continuó este turno. El front lo reenvía en el siguiente
   *  POST /chat para seguir el mismo hilo (conversacion.iniciada). */
  conversacion_id?: string
  /** CAM-3: tools que el agente consultó (con su input). IHC: de aquí salen las
   *  instancias accionables (las que el agente tocó por instancia_id). */
  traza?: Array<{ tool: string; input?: any; ok?: boolean }>
  /** MP5-FRONT-CONFIRMAR (96b62f45): propuesta del orquestador chat→crear-entidad (campo
   *  top-level dedicado, shape MF-SHAPE-RATIFICADO — la UI no escarba la traza). */
  propuesta_entidad?: PropuestaEntidad
}

/**
 * IHC mitad-2 (74043ee7): instancias ACCIONABLES de una respuesta del agente.
 * Dos fuentes, misma gobernanza:
 *   1. La TRAZA de tools (input.instancia_id de get_instance/actions/fields) — como f13222da.
 *   2. Los UUID que el agente CITA en su texto (query_instances no lleva instancia_id en el
 *      input, así que sus resultados solo aparecen aquí; sin esta fuente la superficie ofrecía
 *      MENOS de lo que el permiso autoriza).
 * La gobernanza no cambia: cada id pasa por get_available_actions/fields server-side
 * (keystone campo-UUID); sin verbos ni campos concedidos → AccionesInstancia no renderiza
 * nada. El cap acota las llamadas MCP por mensaje (2 por instancia).
 */
const MAX_INSTANCIAS_ACCIONABLES = 8
const RE_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

function instanciasAccionables(resp: { respuesta: string; traza?: ChatResponse['traza'] }): string[] {
  const deTraza = (resp.traza || [])
    .map((t) => t?.input?.instancia_id)
    .filter((x): x is string => typeof x === 'string' && /^[0-9a-f-]{36}$/i.test(x))
  const delTexto = (resp.respuesta.match(RE_UUID) || []).map((u) => u.toLowerCase())
  return [...new Set([...deTraza.map((u) => u.toLowerCase()), ...delTexto])].slice(0, MAX_INSTANCIAS_ACCIONABLES)
}

/**
 * Separa los bloques ```html del resto del texto. Devuelve los segmentos en orden
 * para preservar el render (texto · informe · texto…). El cerebro emite el informe
 * como un fenced block estándar; aquí solo lo extraemos para presentarlo.
 */
interface Segmento { tipo: 'texto' | 'html'; contenido: string }
const RE_HTML_FENCE = /```html\s*\n([\s\S]*?)```/gi

function segmentar(content: string): Segmento[] {
  const segs: Segmento[] = []
  let ultimo = 0
  let m: RegExpExecArray | null
  RE_HTML_FENCE.lastIndex = 0
  while ((m = RE_HTML_FENCE.exec(content)) !== null) {
    if (m.index > ultimo) {
      const t = content.slice(ultimo, m.index).trim()
      if (t) segs.push({ tipo: 'texto', contenido: t })
    }
    const html = (m[1] || '').trim()
    if (html) segs.push({ tipo: 'html', contenido: html })
    ultimo = m.index + m[0].length
  }
  if (ultimo < content.length) {
    const t = content.slice(ultimo).trim()
    if (t) segs.push({ tipo: 'texto', contenido: t })
  }
  return segs.length ? segs : [{ tipo: 'texto', contenido: content }]
}

/**
 * embedded (PWA-3): el chat-agente como SUPERFICIE PRIMARIA (página completa), no drawer.
 * En hosts de cliente (chat.asesorbi.es) el chat ES la home; en el Arquitecto sigue siendo
 * el drawer lateral (Ctrl+J). Mismo componente, cero duplicación — solo cambia el chrome.
 */
export function ChatPanel({ embedded = false }: { embedded?: boolean } = {}) {
  const token = useAuthStore((s) => s.token)
  const openStore = useChatStore((s) => s.open)
  const open = embedded || openStore
  const setOpen = useChatStore((s) => s.setOpen)
  const loading = useChatStore((s) => s.loading)
  const setLoading = useChatStore((s) => s.setLoading)
  const mensajes = useChatStore((s) => s.mensajes)
  const append = useChatStore((s) => s.append)
  const resetMensajes = useChatStore((s) => s.reset)
  const incluirContexto = useChatStore((s) => s.incluirContexto)
  const setIncluirContexto = useChatStore((s) => s.setIncluirContexto)
  const conversacionId = useChatStore((s) => s.conversacionId)
  const agenteId = useChatStore((s) => s.agenteId)
  const setConversacionId = useChatStore((s) => s.setConversacionId)
  const setAgenteId = useChatStore((s) => s.setAgenteId)
  const setHilo = useChatStore((s) => s.setHilo)

  const [input, setInput] = useState('')
  const [expandido, setExpandido] = useState(false)
  // F3 Inc 2: cajón de conversaciones recuperables (hilos del usuario en su CO).
  const [mostrarHilos, setMostrarHilos] = useState(false)
  // F3 Inc 4: id del informe gobernado cuyo html_resultado se está cargando (spinner del botón).
  const [cargandoInforme, setCargandoInforme] = useState<string | null>(null)
  // AF-1: el informe (html) que se muestra en el visor full-page (null = cerrado).
  const [informeHtml, setInformeHtml] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const params = useParams()

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [open, mensajes.length, loading])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (loading || !input.trim()) return

    const msgUser: ChatMessage = { role: 'user', content: input.trim(), ts: Date.now() }
    append(msgUser)
    setInput('')
    setLoading(true)

    // Historial: últimos 10 mensajes anteriores (sin el nuevo)
    const historial = mensajes.slice(-10).map((m) => ({ role: m.role, content: m.content }))

    // Contexto derivado de ruta actual
    const contexto: any = {}
    if (incluirContexto) {
      contexto.ruta = location.pathname
      if (params.subdominio && params.token && params.estado) {
        contexto.entidad_nombre = `AARPIA.${params.subdominio}.${params.token}.${params.estado}`
      }
      if (params.instanciaId) {
        contexto.instancia_id = params.instanciaId
      }
    }

    try {
      const resp = await api.post<ChatResponse>('/chat', token, {
        mensaje: msgUser.content,
        contexto: Object.keys(contexto).length > 0 ? contexto : undefined,
        historial,
        // F3: continúa el hilo gobernado (null → el back abre uno nuevo y lo devuelve).
        conversacion_id: conversacionId ?? undefined,
        // F3: modelo elegido en el selector (undefined → default del back).
        agente_id: agenteId ?? undefined,
      })
      // F3: fija/persiste el hilo que el back abrió o continuó.
      if (resp.conversacion_id) setConversacionId(resp.conversacion_id)
      append({
        role: 'assistant',
        content: resp.respuesta,
        ts: Date.now(),
        tokens: resp.tokens,
        latencia_ms: resp.latencia_ms,
        instancias: instanciasAccionables(resp),
        propuesta: resp.propuesta_entidad,
        // #1 trazabilidad visible: los pasos de lectura/propuesta de este turno.
        traza: resp.traza,
      })
    } catch (e: any) {
      // E2E-CHAT-FAILED-TO-FETCH (orden 5c1d1e04): distinguir el fallo de RED (la
      // promesa de fetch rechaza con TypeError "Failed to fetch" → la conexión murió
      // ANTES de una respuesta HTTP: típicamente un informe grande cuya generación
      // LLM excede el read-timeout aguas arriba — el chat es request/response no-stream)
      // de un error HTTP del backend (ApiError, p.ej. 502 del provider, que SÍ trae
      // detalle). Nunca volcar "Failed to fetch" crudo: explicar y dar salida.
      const esFalloRed = !(e instanceof ApiError) && (e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(String(e?.message || '')))
      if (esFalloRed) {
        const aviso =
          'La respuesta no llegó a completarse (la conexión se cortó antes de recibirla). ' +
          'Suele pasar con informes muy extensos cuya generación tarda demasiado. ' +
          'Pídelo por partes o acota el alcance (p. ej. una sección o un periodo) y reintento.'
        append({ role: 'assistant', content: `_${aviso}_`, ts: Date.now() })
        toast.error('La respuesta se cortó; pide el informe por partes')
      } else {
        const { mensaje } = explicarError(e)
        append({ role: 'assistant', content: `_Error: ${mensaje}_`, ts: Date.now() })
        toastError(e, 'Chat')
      }
    } finally {
      setLoading(false)
    }
  }

  // F3 Inc 2: recuperar un hilo gobernado. Mapea los turnos (consulta-ia.respondida) a mensajes
  // del chat — pregunta→user, respuesta→assistant. El render existente (segmentar) muestra el
  // botón de informe si la respuesta trae el bloque ```html; instanciasAccionables mantiene el
  // camino B vivo sobre los UUID citados. El front NO transita nada: solo presenta lo leído.
  async function abrirHilo(id: string) {
    try {
      const hilo = await cargarConversacion(id, token)
      // Informe gobernado (estado generado) por turno que lo disparó (§16, trace_chain).
      const informePorConsulta = new Map<string, string>()
      for (const inf of hilo.informes) {
        if (inf.current_state === 'generado') informePorConsulta.set(inf.consulta_id, inf.id)
      }
      const msgs: ChatMessage[] = []
      for (const c of hilo.consultas) {
        const ts = c.ts ? (Date.parse(c.ts) || Date.now()) : Date.now()
        if (c.pregunta) msgs.push({ role: 'user', content: c.pregunta, ts })
        if (c.respuesta) {
          const informeId = informePorConsulta.get(c.id)
          // Si hay informe gobernado, lo abrimos por su html_resultado (fuente de verdad) y quitamos
          // la copia ```html inline para no duplicar el botón. Sin informe (p.ej. §16 falló), se deja
          // el inline como fallback (segmentar lo presenta).
          const content = informeId ? c.respuesta.replace(/```html\s*\n[\s\S]*?```/gi, '').trim() : c.respuesta
          msgs.push({ role: 'assistant', content, ts, instancias: instanciasAccionables({ respuesta: c.respuesta }), informeId })
        }
      }
      setHilo(id, msgs)
      setMostrarHilos(false)
    } catch (e: any) {
      toastError(e, 'Conversación')
    }
  }

  // F3 Inc 4: abre el informe GOBERNADO recuperado por su html_resultado (no la copia inline).
  async function abrirInformeGobernado(informeId: string) {
    setCargandoInforme(informeId)
    try {
      const html = await cargarHtmlInforme(informeId, token)
      if (html) setInformeHtml(html)
      else toast.error('El informe no tiene contenido disponible.')
    } catch (e: any) {
      toastError(e, 'Informe')
    } finally {
      setCargandoInforme(null)
    }
  }

  if (!open) return null

  // embedded → ocupa el contenedor (la home del cliente); si no → drawer derecho
  // expandible (AF-1). Mismo cuerpo (mensajes + input + visor) en ambos casos.
  const panelClase = embedded
    ? 'relative flex flex-col h-full w-full bg-background'
    : expandido
      ? 'fixed right-0 top-0 z-50 h-screen w-full bg-background shadow-xl flex flex-col'
      : 'fixed right-0 top-0 z-50 h-screen w-full sm:w-[480px] bg-background shadow-xl flex flex-col'

  return (
    <>
      {!embedded && <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />}
      <div className={panelClase}>
        <div className="p-4 flex items-center justify-between gap-2 shadow-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <div className="min-w-0">
              <div className="font-semibold text-sm">Asistente</div>
              <SelectorModelo token={token} agenteId={agenteId} onElegir={setAgenteId} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMostrarHilos((v) => !v)}
              title="Conversaciones"
              aria-label="Ver tus conversaciones"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { resetMensajes(); setMostrarHilos(false); toast.success('Nueva conversación') }}
              title="Nueva conversación"
              aria-label="Empezar una conversación nueva"
            >
              <Plus className="h-4 w-4" />
            </Button>
            {!embedded && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setExpandido((v) => !v)}
                title={expandido ? 'Restaurar tamaño' : 'Pantalla completa'}
                aria-label={expandido ? 'Restaurar tamaño del asistente' : 'Expandir el asistente a pantalla completa'}
              >
                {expandido ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            )}
            {!embedded && (
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} title="Cerrar (Esc)" aria-label="Cerrar el asistente">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {mensajes.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 space-y-3">
              <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <div>
                <div className="font-medium text-foreground">¿En qué te ayudo?</div>
                <div className="text-xs mt-2 max-w-sm mx-auto">
                  Puedo explicar cualquier módulo, sugerir el asistente correcto para una tarea,
                  resolver dudas sobre el funcionamiento de la aplicación y proponer estructuras
                  de módulos nuevos.
                </div>
              </div>
              <div className="text-xs space-y-1 max-w-sm mx-auto text-left">
                <div className="text-muted-foreground">Sugerencias:</div>
                <button
                  onClick={() => setInput('¿Cómo creo un módulo nuevo?')}
                  className="block w-full text-left px-3 py-2.5 rounded-lg bg-card shadow-sm hover:bg-accent hover:shadow transition-all"
                >
                  ¿Cómo creo un módulo nuevo?
                </button>
                <button
                  onClick={() => setInput('¿Qué diferencia hay entre una contratación y un acceso compartido?')}
                  className="block w-full text-left px-3 py-2.5 rounded-lg bg-card shadow-sm hover:bg-accent hover:shadow transition-all"
                >
                  ¿Qué diferencia hay entre una contratación y un acceso compartido?
                </button>
                <button
                  onClick={() => setInput('¿Por qué no puedo modificar una ficha cerrada?')}
                  className="block w-full text-left px-3 py-2.5 rounded-lg bg-card shadow-sm hover:bg-accent hover:shadow transition-all"
                >
                  ¿Por qué no puedo modificar una ficha cerrada?
                </button>
              </div>
            </div>
          ) : (
            mensajes.map((m, i) => (
              <Mensaje
                key={i}
                m={m}
                onAbrirInforme={setInformeHtml}
                onAbrirInformeGobernado={abrirInformeGobernado}
                cargandoInformeId={cargandoInforme}
              />
            ))
          )}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              El asistente está pensando...
            </div>
          )}
        </div>

        <div className="bg-muted/30 p-3 space-y-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={incluirContexto}
              onChange={(e) => setIncluirContexto(e.target.checked)}
              className="w-3 h-3"
            />
            <span>Incluir lo que estás viendo (ruta + módulo)</span>
          </label>
          <form onSubmit={handleSubmit} className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e as any)
                }
              }}
              placeholder="Escribe tu pregunta... (Enter envía · Shift+Enter salto de línea)"
              rows={2}
              disabled={loading}
              className="flex-1 px-3 py-2 text-sm rounded-lg bg-background shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none disabled:opacity-50"
            />
            <Button type="submit" disabled={loading || !input.trim()} size="icon" className="self-end" title="Enviar" aria-label="Enviar mensaje">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
        {mostrarHilos && (
          <ListaConversaciones
            token={token}
            activoId={conversacionId}
            onSelect={abrirHilo}
            onNueva={() => { resetMensajes(); setMostrarHilos(false) }}
            onClose={() => setMostrarHilos(false)}
          />
        )}
      </div>

      {informeHtml && <VisorInforme html={informeHtml} onClose={() => setInformeHtml(null)} />}
    </>
  )
}

/** Logo del proveedor: avatar_url si lo hay; si no, una marca de relieve con la inicial del
 *  proveedor (sin emojis, color por proveedor conocido o brand por defecto). */
const COLOR_PROVEEDOR: Record<string, string> = {
  anthropic: 'bg-[#d97757] text-white',
  openai: 'bg-[#10a37f] text-white',
  google: 'bg-[#4285f4] text-white',
  mistral: 'bg-[#fa520f] text-white',
  groq: 'bg-[#f55036] text-white',
  ollama: 'bg-foreground text-background',
}
function LogoProveedor({ agente, size = 'sm' }: { agente: AgenteModelo | null; size?: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'h-7 w-7 text-xs' : 'h-5 w-5 text-[10px]'
  if (agente?.avatar_url) {
    return <img src={agente.avatar_url} alt="" className={`${dim} rounded-md object-cover flex-shrink-0`} />
  }
  const cls = COLOR_PROVEEDOR[agente?.proveedor?.toLowerCase() || ''] || 'bg-primary text-primary-foreground'
  const inicial = (agente?.proveedor || agente?.nombre || '?').charAt(0).toUpperCase()
  return <span className={`${dim} ${cls} rounded-md inline-flex items-center justify-center font-semibold flex-shrink-0`}>{inicial}</span>
}

/**
 * F3 Inc 3 — selector de modelo. Lista los agente-ia.registrado activos y consumibles del CO
 * (lib/agentes) con su logo; el elegido se reenvía como agente_id en el POST /chat. null =
 * modelo por defecto del back. Cero hardcode de modelos: deriva del catálogo gobernado.
 */
function SelectorModelo({ token, agenteId, onElegir }: {
  token: string | null
  agenteId: string | null
  onElegir: (id: string | null) => void
}) {
  const [abierto, setAbierto] = useState(false)
  const [agentes, setAgentes] = useState<AgenteModelo[] | null>(null)
  useEffect(() => {
    let vivo = true
    listarAgentesActivos(token).then((a) => { if (vivo) setAgentes(a) }).catch(() => { if (vivo) setAgentes([]) })
    return () => { vivo = false }
  }, [token])
  const actual = agentes?.find((a) => a.id === agenteId) || null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="-mx-1.5 flex items-center gap-1.5 rounded-lg px-1.5 py-0.5 hover:bg-accent transition-colors max-w-[200px]"
        title="Elegir modelo"
        aria-label="Elegir modelo de IA"
      >
        <LogoProveedor agente={actual} />
        <span className="text-[11px] font-medium text-muted-foreground truncate">{actual ? actual.nombre : 'Modelo por defecto'}</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      </button>
      {abierto && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setAbierto(false)} />
          <div className="absolute left-0 z-30 mt-1 w-64 rounded-xl bg-card shadow-lg p-1 max-h-72 overflow-y-auto">
            {agentes === null ? (
              <div className="p-2 space-y-1">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-muted/40 animate-pulse" />)}</div>
            ) : agentes.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">No hay modelos disponibles para tu organización.</div>
            ) : (
              agentes.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { onElegir(a.id); setAbierto(false) }}
                  className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${a.id === agenteId ? 'bg-accent' : 'hover:bg-accent/60'}`}
                >
                  <LogoProveedor agente={a} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{a.nombre}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{[a.proveedor, a.modelo].filter(Boolean).join(' · ')}</div>
                  </div>
                  {a.id === agenteId && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** Fecha relativa compacta para la lista de hilos (hoy → hora, ayer, hace N días, fecha corta). */
function fmtFechaRel(iso?: string): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const difDias = Math.floor((Date.now() - t) / 86400000)
  if (difDias <= 0) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  if (difDias === 1) return 'ayer'
  if (difDias < 7) return `hace ${difDias} días`
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
}

/**
 * F3 Inc 2 — cajón de conversaciones recuperables. Lista los hilos del usuario en su CO
 * (GET /mis-conversaciones, ordenados por el back) y abre el seleccionado. Cero lógica de
 * negocio: presenta lo que el endpoint devuelve. Estética del design system (relieve por
 * sombra, sin líneas, claro+oscuro por las variables del theme).
 */
function ListaConversaciones({ token, activoId, onSelect, onNueva, onClose }: {
  token: string | null
  activoId: string | null
  onSelect: (id: string) => void
  onNueva: () => void
  onClose: () => void
}) {
  const [items, setItems] = useState<ConversacionResumen[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let vivo = true
    listarConversaciones(token)
      .then((r) => { if (vivo) setItems(r.data || []) })
      .catch((e) => { if (vivo) setError(explicarError(e).mensaje) })
    return () => { vivo = false }
  }, [token])

  return (
    <div className="absolute inset-0 z-20 flex">
      <div className="w-full sm:w-80 h-full bg-card shadow-xl flex flex-col">
        <div className="p-4 flex items-center justify-between gap-2 shadow-sm">
          <div className="font-semibold text-sm">Conversaciones</div>
          <Button variant="ghost" size="icon" onClick={onClose} title="Cerrar" aria-label="Cerrar conversaciones">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-3">
          <button
            onClick={onNueva}
            className="w-full flex items-center gap-2 rounded-lg bg-card px-3 py-2.5 text-left text-sm shadow-sm hover:bg-accent hover:shadow transition-all"
          >
            <Plus className="h-4 w-4 text-primary" />
            <span className="font-medium">Nueva conversación</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
          {items === null && !error ? (
            <div className="space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-muted/40 animate-pulse" />)}
            </div>
          ) : error ? (
            <div className="text-xs text-muted-foreground py-6 text-center">{error}</div>
          ) : items && items.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 space-y-2">
              <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <div className="text-xs">Aún no tienes conversaciones.</div>
            </div>
          ) : (
            items!.map((c) => (
              <button
                key={c.id}
                onClick={() => onSelect(c.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg shadow-sm transition-all hover:shadow ${c.id === activoId ? 'bg-accent' : 'bg-card hover:bg-accent/60'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate flex-1">{c.titulo?.trim() || 'Conversación'}</span>
                  <span className="text-[11px] text-muted-foreground flex-shrink-0">{fmtFechaRel(c.ultimo_mensaje_ts)}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {c.n_consultas} {c.n_consultas === 1 ? 'mensaje' : 'mensajes'}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
    </div>
  )
}

/**
 * #1 trazabilidad visible: traduce un paso de tool-use a lenguaje del usuario. Las tools de
 * LECTURA narran qué consultó; create_instance/transition se etiquetan como PROPUESTAS no
 * ejecutadas (frontera dual). Cero acceso a datos: solo describe la traza que el back ya devolvió.
 */
function describirPaso(tool: string, input: unknown): string {
  const inp = (input || {}) as Record<string, any>
  const ent = inp.entidad_nombre || inp.entidad || inp.nombre
  const entCorta = typeof ent === 'string' ? ent.split('.').slice(1).join('.') : null
  const id = typeof inp.instancia_id === 'string' ? inp.instancia_id.slice(0, 8) : null
  switch (tool) {
    case 'query_catalog': return 'Consultó el catálogo de entidades'
    case 'get_definition': return `Leyó la definición${entCorta ? ` de ${entCorta}` : ''}`
    case 'query_instances': return `Buscó instancias${entCorta ? ` de ${entCorta}` : ''}`
    case 'get_instance': return `Leyó una instancia${id ? ` (${id})` : ''}`
    case 'get_available_actions': return `Consultó qué acciones puedes hacer${id ? ` sobre ${id}` : ''}`
    case 'get_available_fields': return `Consultó los campos editables${id ? ` de ${id}` : ''}`
    case 'trace_chain': return `Trazó el origen causal${id ? ` de ${id}` : ''}`
    case 'get_token_life': return `Trazó la vida del token${id ? ` (${id})` : ''}`
    case 'get_children': return `Buscó lo que se disparó${id ? ` desde ${id}` : ''}`
    case 'resolver_capacidades': return `Resolvió qué puedes hacer${typeof inp.pregunta === 'string' ? `: "${inp.pregunta.slice(0, 40)}"` : ''}`
    case 'proponer_entidad': return 'Compuso una propuesta de entidad (sin crear nada)'
    case 'crear_borrador_entidad': return 'Creó un borrador de entidad (descartable, lo validas tú)'
    case 'create_instance': return 'Propuso crear una instancia (no ejecutado — lo confirmas tú)'
    case 'transition': return 'Propuso una transición (no ejecutado — lo confirmas tú)'
    default: return tool
  }
}

function Mensaje({ m, onAbrirInforme, onAbrirInformeGobernado, cargandoInformeId }: {
  m: ChatMessage
  onAbrirInforme: (html: string) => void
  onAbrirInformeGobernado: (informeId: string) => void
  cargandoInformeId: string | null
}) {
  const isUser = m.role === 'user'
  // #1 trazabilidad visible: panel desplegable con los pasos de consulta del asistente (colapsado).
  const [verTraza, setVerTraza] = useState(false)
  // AF-1: solo los mensajes del asistente pueden traer informes (```html). El del
  // usuario se pinta siempre como texto (no interpretamos su entrada como informe).
  const segmentos = useMemo(() => (isUser ? null : segmentar(m.content)), [isUser, m.content])

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 shadow-sm ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
        {isUser || !segmentos ? (
          <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
        ) : (
          <div className="space-y-2">
            {segmentos.map((seg, i) =>
              seg.tipo === 'texto' ? (
                seg.contenido ? <div key={i} className="text-sm whitespace-pre-wrap break-words">{seg.contenido}</div> : null
              ) : (
                <button
                  key={i}
                  onClick={() => onAbrirInforme(seg.contenido)}
                  className="w-full flex items-center gap-2 rounded-lg bg-card px-3 py-2.5 text-left text-sm shadow-md hover:bg-accent hover:shadow-lg transition-all"
                >
                  <FileText className="h-4 w-4 flex-shrink-0 text-primary" />
                  <span className="font-medium">Informe generado</span>
                  <span className="ml-auto text-xs text-muted-foreground">Abrir a pantalla completa</span>
                </button>
              ),
            )}
          </div>
        )}
        {/* F3 Inc 4: informe GOBERNADO recuperado — abre su html_resultado (no la copia inline).
            Solo en turnos recuperados con un informe-html.generado nacido (§16). */}
        {!isUser && m.informeId && (
          <button
            onClick={() => onAbrirInformeGobernado(m.informeId!)}
            disabled={cargandoInformeId === m.informeId}
            className="mt-2 w-full flex items-center gap-2 rounded-lg bg-card px-3 py-2.5 text-left text-sm shadow-md hover:bg-accent hover:shadow-lg transition-all disabled:opacity-60"
          >
            <FileText className="h-4 w-4 flex-shrink-0 text-primary" />
            <span className="font-medium">Informe generado</span>
            <span className="ml-auto text-xs text-muted-foreground">{cargandoInformeId === m.informeId ? 'Abriendo…' : 'Abrir a pantalla completa'}</span>
          </button>
        )}
        {/* IHC (f13222da): superficie VIVA — por cada instancia que el agente consultó,
            los controles que el permiso del actor autoriza (ejecución vía API canónica). */}
        {!isUser && m.instancias && m.instancias.length > 0 && (
          <div className="space-y-1.5">
            {m.instancias.map((id) => <AccionesInstancia key={id} instanciaId={id} confirmarAntes />)}
          </div>
        )}
        {/* MP5-FRONT-CONFIRMAR: propuesta del orquestador (preview con semáforo, o borrador
            con Confirmar/Descartar). El VALIDAR lo dispara el humano desde aquí (gate 1). */}
        {!isUser && m.propuesta && (
          <div className="mt-1.5">
            <PropuestaEntidadCard propuesta={m.propuesta} />
          </div>
        )}
        {/* #1 trazabilidad visible: qué consultó el asistente (lectura) en este turno. Hace
            explícita la frontera dual junto a los verbos propuestos (AccionesInstancia). */}
        {!isUser && m.traza && m.traza.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setVerTraza((v) => !v)}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${verTraza ? 'rotate-90' : ''}`} />
              {m.traza.length} {m.traza.length === 1 ? 'consulta' : 'consultas'} al organismo
            </button>
            {verTraza && (
              <div className="mt-1.5 rounded-xl bg-muted/40 p-2.5 space-y-1.5">
                {m.traza.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <span className={`mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0 ${t.ok === false ? 'bg-destructive' : 'bg-primary'}`} />
                    <span className="text-muted-foreground">{describirPaso(t.tool, t.input)}</span>
                  </div>
                ))}
                <div className="pt-1 text-[10px] text-muted-foreground/80">
                  El asistente solo lee y propone; las acciones las confirmas tú y el servidor las revalida.
                </div>
              </div>
            )}
          </div>
        )}
        {!isUser && (m.latencia_ms || m.tokens) && (
          <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-3">
            {m.latencia_ms && (
              <span className="inline-flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" />
                {(m.latencia_ms / 1000).toFixed(1)}s
              </span>
            )}
            {m.tokens && (
              <span className="inline-flex items-center gap-0.5">
                <Cpu className="h-2.5 w-2.5" />
                {m.tokens.input}→{m.tokens.output} tok
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Visor de informe a pantalla completa (AF-1, patrón VisorAppPage): el HTML del
 * cerebro se renderiza en iframe srcDoc (sandbox: ejecuta su gráfico inline pero no
 * accede a la sesión de la PWA). Acciones: Descargar PDF (print-to-PDF del navegador
 * sobre el iframe), Compartir (Web Share API con fallback a copiar el HTML).
 */
// AF-3 + AC-3: puentes inyectados al srcDoc, PARAMETRIZADOS por el UUID de la intención
// del catálogo vivo (el artefacto jamás lleva vocabulario — restricción de la orden:
// las intenciones las inyecta el SHELL). Compat UNA versión con los literales legacy.
//
// Print (shell→iframe): el iframe solo reacciona al mensaje del parent llamando
// window.print() — no lee nada, no expone nada. Acción (iframe→shell): captura clicks
// sobre el contrato (data-instancia-id + data-verbo) y PIDE; cero capacidad dentro.
function construirPuentePrint(uuidImprimir: string | null): string {
  const cond = uuidImprimir
    ? `e&&(e.data==="__aarpia_print__"||(e.data&&e.data.tipo==="${uuidImprimir}"))`
    : 'e&&e.data==="__aarpia_print__"'
  return `<script>window.addEventListener("message",function(e){if(${cond}){try{window.focus()}catch(_){}window.print()}});<\/script>`
}

function construirPuenteAccion(uuidAccion: string | null): string {
  const tipo = uuidAccion || '__aarpia_accion__'
  return `<script>document.addEventListener("click",function(ev){var t=ev.target;if(!t||!t.closest)return;var el=t.closest("[data-instancia-id][data-verbo]");if(!el)return;ev.preventDefault();parent.postMessage({tipo:"${tipo}",instancia_id:String(el.getAttribute("data-instancia-id")||""),verbo:String(el.getAttribute("data-verbo")||"")},"*");});<\/script>`
}

const escHtml = (s: string) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))

/**
 * Cabecera de auditoría — SOLO EN EL PDF (impresión), NO en el informe en pantalla.
 * marca + organización (Context Owner) + usuario + fecha. Va con `@media print`:
 * oculta en el visor, visible solo al imprimir/descargar (display lo controla la clase,
 * no inline, para que la regla de print mande). Maquetada — marca en color, metadatos
 * alineados a la derecha.
 */
function cabeceraAuditoria(opts: { marca: string; color: string; co: string; usuario: string }): string {
  const { marca, color, co, usuario } = opts
  const fecha = new Date().toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })
  return `<style>.aarpia-pdf-header{display:none;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;align-items:flex-end;justify-content:space-between;gap:16px;padding:18px 22px;margin:0 0 24px;border-bottom:3px solid ${escHtml(color)}}@media print{.aarpia-pdf-header{display:flex}}</style>
<header class="aarpia-pdf-header">
  <div>
    <div style="font-size:20px;font-weight:800;letter-spacing:-.01em;color:${escHtml(color)}">${escHtml(marca)}</div>
    <div style="font-size:12px;color:#6b7280;margin-top:2px">Informe</div>
  </div>
  <div style="text-align:right;font-size:11.5px;color:#374151;line-height:1.75">
    <div><span style="color:#9ca3af">Organización&nbsp;&nbsp;</span><strong>${escHtml(co)}</strong></div>
    <div><span style="color:#9ca3af">Usuario&nbsp;&nbsp;</span><strong>${escHtml(usuario)}</strong></div>
    <div><span style="color:#9ca3af">Descargado&nbsp;&nbsp;</span>${escHtml(fecha)}</div>
  </div>
</header>`
}

/** Inserta la cabecera dentro del <body> del informe (o al principio si no hay body). */
function inyectarCabecera(html: string, cabecera: string): string {
  if (/<body[^>]*>/i.test(html)) return html.replace(/<body[^>]*>/i, (m) => m + cabecera)
  return cabecera + html
}

function VisorInforme({ html, onClose }: { html: string; onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const branding = useBrandingStore((s) => s.activo)
  const co = contextOwners.find((c) => c.id === activeCoId)?.nombre || '—'
  const usuario = user?.nombre?.trim() || user?.email || '—'
  // Una acción HOP en vuelo a la vez (anti click-storm desde el HTML del LLM).
  const accionEnCursoRef = useRef(false)
  // AC-3: el vocabulario del puente sale del CATÁLOGO vivo (intencion-puente) — los
  // UUIDs se inyectan al construir los puentes; sin catálogo (fallo de red), los
  // puentes caen al literal legacy (compat UNA versión) y el broker los acepta igual.
  const [catalogo, setCatalogo] = useState<CatalogoIntenciones | null>(null)
  useEffect(() => {
    obtenerCatalogoIntenciones(token).then(setCatalogo).catch(() => {
      logAarpia.warn('Puente: catálogo de intenciones no disponible — puentes en modo legacy esta sesión.')
    })
  }, [token])
  // El doc del informe = cabecera de auditoría + HTML del cerebro + puentes (print AF-3 + acción HOP-2).
  const docFinal = useMemo(
    () => inyectarCabecera(html, cabeceraAuditoria({
      marca: branding.nombre_comercial || 'AARPIA',
      color: branding.color_primario || '#1e40af',
      co, usuario,
    }))
      + construirPuentePrint(catalogo?.porAlias.get('imprimir')?.id || null)
      + construirPuenteAccion(catalogo?.porAlias.get('accion')?.id || null),
    [html, branding.nombre_comercial, branding.color_primario, co, usuario, catalogo],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // HOP-3 (f6b8abd7): BROKER de acciones del informe. El HTML jamás es fuente de
  // permiso — aquí el shell DECIDE: (1) solo mensajes de NUESTRO iframe; (2) allowlist
  // estricta de forma (todo lo demás → WARN estructurado, nunca un error inducible
  // desde el HTML); (3) re-verificación contra get_available_actions del usuario
  // (no concedido → descarte silencioso con WARN); (4) confirmación en SUPERFICIE DE
  // CONFIANZA (toast del shell con el verbo/instancia REALES del servidor — neutraliza
  // label-spoofing: un botón que diga "Ver detalles" con data-verbo=ELIMINAR muestra
  // ELIMINAR aquí); (5) ejecución por la madre única (lib/acciones) — el enforce
  // re-verifica server-side.
  useEffect(() => {
    async function onMensaje(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return // solo nuestro iframe
      // AC-3 broker v2: validación contra el CATÁLOGO (tipo=UUID ∈ habilitadas + shape
      // declarado con vocabulario cerrado). Compat legacy UNA versión dentro del validador.
      if (!catalogo) return // sin catálogo no hay vocabulario: nada que aceptar
      const v = validarMensajeIntencion(e.data, catalogo)
      if (!v.ok) {
        // Mensajes que ni siquiera lo intentan (ruido de extensiones) no se loguean.
        const tipo = (e.data as any)?.tipo
        if (typeof tipo === 'string' && (tipo.startsWith('__aarpia') || /^[0-9a-f-]{36}$/i.test(tipo))) {
          logAarpia.warn('Broker v2: mensaje de intención descartado:', v.motivo)
        }
        return
      }
      // El visor de informes solo opera la intención `accion` (leer/listar/chat son de
      // los módulos de app — AC-3 entrega 2).
      if (v.intencion.alias !== 'accion') {
        logAarpia.warn(`Broker v2: intención '${v.intencion.alias}' no aplicable en el visor de informes — descartada`)
        return
      }
      const instanciaId = v.valores.instancia_id
      const verbo = v.valores.verbo
      if (accionEnCursoRef.current) {
        logAarpia.warn('Broker v2: acción ignorada — ya hay una en curso')
        return
      }
      accionEnCursoRef.current = true
      try {
        const info = await getAvailableActions(instanciaId, token)
        if (!info || !info.acciones_disponibles.includes(verbo)) {
          logAarpia.warn(`Broker v2: acción no concedida descartada (${verbo} sobre ${instanciaId.slice(0, 8)})`)
          return
        }
        const entidadLegible = info.entidad.split('.').slice(1).join('.')
        const ejecutar = async () => {
          try {
            await ejecutarVerboCanonico(info.entidad, instanciaId, verbo, token, 'hop')
            toast.success(`${verbo} ejecutado · la instancia cambió de estado`)
          } catch (err: any) {
            toastError(err, `${verbo} rechazado`)
          }
        }
        // Anillo 4 por DECLARACIÓN de la intención (antes hardcodeado true para acción).
        if (v.intencion.requiere_confirmacion) {
          toast(`El informe pide ${verbo} sobre ${entidadLegible} · ${instanciaId.slice(0, 8)}`, {
            description: `Estado actual: ${info.current_state}. El servidor volverá a validar la transición.`,
            action: { label: `Ejecutar ${verbo}`, onClick: ejecutar },
          })
        } else {
          await ejecutar()
        }
      } finally {
        accionEnCursoRef.current = false
      }
    }
    window.addEventListener('message', onMensaje)
    return () => window.removeEventListener('message', onMensaje)
  }, [token])

  function descargarPdf() {
    // AF-3: print-to-PDF SIN allow-same-origin. El iframe corre en origen OPACO, así
    // que el parent NO puede llamar contentWindow.print() (SecurityError) — el print lo
    // dispara el PROPIO iframe vía un puente postMessage (PRINT_BRIDGE inyectado en el
    // srcDoc). El navegador abre su diálogo → "Guardar como PDF". Consistente entre
    // navegadores y sin escape de sandbox.
    try {
      // AC-3: el print viaja con el UUID de la intención `imprimir` (shell→iframe);
      // sin catálogo, literal legacy (el puente inyectado acepta ambos — compat 1 versión).
      const uuidImprimir = catalogo?.porAlias.get('imprimir')?.id
      iframeRef.current?.contentWindow?.postMessage(uuidImprimir ? { tipo: uuidImprimir } : '__aarpia_print__', '*')
    } catch {
      toast.error('No se pudo abrir el diálogo de impresión; usa Ctrl+P sobre el informe.')
    }
  }

  async function compartir() {
    try {
      // Comparte el HTML del informe puro (la cabecera de auditoría es solo del PDF).
      const file = new File([html], 'informe-aarpia.html', { type: 'text/html' })
      const nav = navigator as any
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Informe AARPIA' })
        return
      }
      await navigator.clipboard.writeText(html)
      toast.success('HTML del informe copiado al portapapeles')
    } catch (e: any) {
      if (e?.name !== 'AbortError') toast.error('No se pudo compartir el informe')
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col">
      <div className="flex items-center gap-2 p-3 shadow-sm">
        <FileText className="h-4 w-4 text-primary" />
        <span className="font-semibold text-sm">Informe</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={descargarPdf} title="Descargar PDF">
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Descargar PDF</span>
          </Button>
          <Button variant="outline" size="sm" onClick={compartir} title="Compartir">
            <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">Compartir</span>
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} title="Cerrar (Esc)" aria-label="Cerrar el informe">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <iframe
        ref={iframeRef}
        title="Informe a pantalla completa"
        // AF-3 (seguridad): srcDoc = HTML del cerebro + PRINT_BRIDGE. SIN allow-same-origin
        // → el iframe corre en ORIGEN OPACO: su localStorage es propio y vacío, no comparte
        // el de la PWA, y su JS NO puede leer parent (el JWT está a salvo aunque el HTML del
        // LLM venga con prompt-injection). allow-scripts: el gráfico (Chart.js/SVG) corre.
        // allow-modals: window.print() del propio iframe abre el diálogo (lo dispara el
        // PRINT_BRIDGE al recibir el postMessage del parent). Sin popups/downloads/same-origin.
        srcDoc={docFinal}
        sandbox="allow-scripts allow-modals"
        className="flex-1 w-full border-0 bg-white"
      />
    </div>
  )
}
